import React from 'react'
import { isEmpty } from 'ramda'
import { connect } from 'react-redux'
import { useFormik } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'
import { BASE_SITE_URL } from 'sidecar-utility-library'
import {
  Bubble,
  Button,
  Card,
  ContentSection,
  Link,
  TextField,
} from 'sidecar-components'
import { yup } from 'sidecar-utilities'

import { PageHeader } from 'components/_common/PageHeader'
import { saveUncoveredApplicant } from 'store/UncoveredApplicant'
import { useHeap } from 'utils/heap/useHeap'

/**
 * @param {string} applicantsError - Error message from the
 *   saveUncoveredApplicant function
 * @param {boolean} busy - Busy state of the saveUncoveredApplicant function
 * @param {function} saveUncoveredApplicant - Function to save the user's info
 *   for later
 * @returns {JSX.Element} - Page with a form for people to get a reminder for
 *   when we offer Access again. Users get here by getting rejected from the
 *   Access funnel.
 */
export const NotAvailable = ({
  applicantsError,
  busy,
  saveUncoveredApplicant,
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Add user properties to Heap tracking for this page.
  useHeap()

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
    },
    validationSchema: yup.object().shape({
      firstName: yup.string().required().label('First name'),
      lastName: yup.string().required().label('Last name'),
      email: yup.string().email().required().label('Email'),
    }),
    onSubmit: async values => {
      const { state, zipcode } = location.state || {}

      const result = await saveUncoveredApplicant({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        state,
        zip: zipcode,
      })

      if (result.error) {
        return
      }

      navigate('/thankyou')
    },
  })

  return (
    <>
      <PageHeader className="text-center">
        <h3 className="my-0">Thank you for your interest in Sidecar Health</h3>
        <h1 className="mt-0 inherit">
          We are not offering individual coverage at this time
        </h1>
      </PageHeader>

      <form onSubmit={formik.handleSubmit}>
        <ContentSection
          testId="info_notAvailable"
          className="md:flex md:flex-row"
        >
          <main className="basis-2/3 max-md:mb-10">
            <Card>
              <h2>Share your email and we{`\u2019`}ll contact you if we do</h2>
              <p>We won{`\u2019`}t reach out for any other reason.</p>

              {applicantsError && (
                <Bubble
                  id="applicantsError"
                  sentiment="risk"
                  message={applicantsError}
                />
              )}

              <TextField
                label="First name"
                name="firstName"
                value={formik.values.firstName}
                testId="input_firstName"
                error={formik.touched.firstName && formik.errors.firstName}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                textRedacted={true}
                className="mb-8"
              />

              <TextField
                name="lastName"
                label="Last name"
                value={formik.values.lastName}
                testId="input_lastName"
                error={formik.touched.lastName && formik.errors.lastName}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                textRedacted={true}
                className="mb-8"
              />

              <TextField
                type="email"
                label="Email"
                name="email"
                value={formik.values.email}
                autoComplete="username"
                testId="input_email"
                error={formik.touched.email && formik.errors.email}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                textRedacted={true}
              />
            </Card>
          </main>
          <aside className="basis-1/3">
            <Card
              footer={
                <p className="sh-body-sm">
                  By clicking "Contact me later", you agree to receive emails
                  from Sidecar Health and accept our{' '}
                  <Link path={`${BASE_SITE_URL}/terms-of-use`} type="external">
                    Terms of Use
                  </Link>{' '}
                  and{' '}
                  <Link
                    path={`${BASE_SITE_URL}/privacy-policy`}
                    type="external"
                  >
                    Privacy Policy
                  </Link>
                  . You can opt-out from receiving emails at any time.
                </p>
              }
            >
              <Button
                width="full"
                size="large"
                icon="forward"
                disabled={!isEmpty(formik.errors)}
                busy={busy || formik.busy}
                type="submit"
                testId="btn_contactMe"
              >
                Contact me later
              </Button>
            </Card>
          </aside>
        </ContentSection>
      </form>
    </>
  )
}

const mapStateToDispatch = ({ UncoveredApplicant }) => ({
  // not sure why but when the error was called just error,
  // it would always be undefined only when I changed the name would I see it
  applicantsError: UncoveredApplicant?.applicants?.error,
  busy: UncoveredApplicant?.applicants?.busy || false,
})

const mapDispatchToProps = {
  saveUncoveredApplicant,
}

export default connect(mapStateToDispatch, mapDispatchToProps)(NotAvailable)
